// noinspection SpellCheckingInspection

import React from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';

const sanitizeSchema = {
  ...defaultSchema,
  tagNames: [
    ...(defaultSchema.tagNames ?? []),
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
    'a', 'sup',
  ],
  attributes: {
    ...(defaultSchema.attributes),
    table: [
      ...(defaultSchema.attributes?.table ?? []),
      'align', 'border', 'cellpadding', 'cellspacing'
    ],
    th: [
      ...(defaultSchema.attributes?.th ?? []),
      'align', 'colspan', 'rowspan', 'scope'
    ],
    td: [
      ...(defaultSchema.attributes?.td ?? []),
      'align', 'colspan', 'rowspan', 'headers'
    ],
    a: [
      ...(defaultSchema.attributes?.a ?? []),
      'href', 'title', 'target', 'rel'
    ],
    // Kept so a `sup` override can tell an ordinary superscript from a citation marker.
    sup: [
      ...(defaultSchema.attributes?.sup ?? []),
      'className'
    ],
  },
};

const MarkdownLink = (props: React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
  const { children, ...rest } = props;
  // target and rel come after the spread, not before it. Both are allowed through the
  // sanitize schema above, so markdown that sets its own `rel` would otherwise win the
  // merge and drop the reverse-tabnabbing guard on a link it also controls.
  return <a {...rest} target="_blank" rel="noopener noreferrer">{children}</a>;
};

type Props = {
  children: string;
  /**
   * Element overrides, for callers that render their own markup inside the content.
   *
   * `a` is not overridable - see below.
   */
  components?: Omit<Components, 'a'>;
};

export default function MarkdownRenderer({ children, components }: Readonly<Props>) {
  return (
    <div className="prose max-w-none">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[
          rehypeRaw,
          [rehypeSanitize, sanitizeSchema]
        ]}
        // The caller's overrides first, so `a` always resolves to MarkdownLink. Spreading
        // them last let a caller replace the anchor and take the target/rel handling with
        // it, which is a security property rather than a styling one.
        components={{ ...components, a: MarkdownLink }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
